"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("./util");
const logger_1 = require("./logger");
const enums_1 = require("./enums");
/**
 * Manages all negotiations between Peers.
 */
class Negotiator {
    constructor(connection) {
        this.connection = connection;
    }
    /** Returns a PeerConnection object set up correctly (for data, media). */
    startConnection(options) {
        const peerConnection = this._startPeerConnection();
        // Set the connection's PC.
        this.connection.peerConnection = peerConnection;
        if (this.connection.type === enums_1.ConnectionType.Media && options._stream) {
            this._addTracksToConnection(options._stream, peerConnection);
        }
        // What do we need to do now?
        if (options.originator) {
            if (this.connection.type === enums_1.ConnectionType.Data) {
                const dataConnection = this.connection;
                const config = { ordered: !!options.reliable };
                const dataChannel = peerConnection.createDataChannel(dataConnection.label, config);
                dataConnection.initialize(dataChannel);
            }
            this._makeOffer();
        }
        else {
            this.handleSDP("OFFER", options.sdp);
        }
    }
    /** Start a PC. */
    _startPeerConnection() {
        logger_1.default.log("Creating RTCPeerConnection.");
        const peerConnection = new RTCPeerConnection(this.connection.config || this.connection.provider.options.config);
        this._setupListeners(peerConnection);
        return peerConnection;
    }
    /** Set up various WebRTC listeners. */
    _setupListeners(peerConnection) {
        const peerId = this.connection.peer;
        const connectionId = this.connection.connectionId;
        const connectionType = this.connection.type;
        const provider = this.connection.provider;
        // ICE CANDIDATES.
        logger_1.default.log("Listening for ICE candidates.");
        peerConnection.onicecandidate = (evt) => {
            if (!evt.candidate || !evt.candidate.candidate)
                return;
            logger_1.default.log(`Received ICE candidates for ${peerId}:`, evt.candidate);
            provider.socket.send({
                type: enums_1.ServerMessageType.Candidate,
                payload: {
                    candidate: evt.candidate,
                    type: connectionType,
                    connectionId: connectionId
                },
                dst: peerId
            });
        };
        peerConnection.oniceconnectionstatechange = () => {
            switch (peerConnection.iceConnectionState) {
                case "failed":
                    logger_1.default.log("iceConnectionState is failed, closing connections to " +
                        peerId);
                    this.connection.emit(enums_1.ConnectionEventType.Error, new Error("Negotiation of connection to " + peerId + " failed."));
                    this.connection.close();
                    break;
                case "closed":
                    logger_1.default.log("iceConnectionState is closed, closing connections to " +
                        peerId);
                    this.connection.emit(enums_1.ConnectionEventType.Error, new Error("Connection to " + peerId + " closed."));
                    this.connection.close();
                    break;
                case "disconnected":
                    logger_1.default.log("iceConnectionState is disconnected, closing connections to " +
                        peerId);
                    this.connection.emit(enums_1.ConnectionEventType.Error, new Error("Connection to " + peerId + " disconnected."));
                    this.connection.close();
                    break;
                case "completed":
                    peerConnection.onicecandidate = util_1.util.noop;
                    break;
            }
            this.connection.emit(enums_1.ConnectionEventType.IceStateChanged, peerConnection.iceConnectionState);
        };
        // DATACONNECTION.
        logger_1.default.log("Listening for data channel");
        // Fired between offer and answer, so options should already be saved
        // in the options hash.
        peerConnection.ondatachannel = (evt) => {
            logger_1.default.log("Received data channel");
            const dataChannel = evt.channel;
            const connection = (provider.getConnection(peerId, connectionId));
            connection.initialize(dataChannel);
        };
        // MEDIACONNECTION.
        logger_1.default.log("Listening for remote stream");
        peerConnection.ontrack = (evt) => {
            logger_1.default.log("Received remote stream");
            const stream = evt.streams[0];
            const connection = provider.getConnection(peerId, connectionId);
            if (connection.type === enums_1.ConnectionType.Media) {
                const mediaConnection = connection;
                this._addStreamToMediaConnection(stream, mediaConnection);
            }
        };
    }
    cleanup() {
        logger_1.default.log("Cleaning up PeerConnection to " + this.connection.peer);
        const peerConnection = this.connection.peerConnection;
        if (!peerConnection) {
            return;
        }
        this.connection.peerConnection = null;
        //unsubscribe from all PeerConnection's events
        peerConnection.onicecandidate = peerConnection.oniceconnectionstatechange = peerConnection.ondatachannel = peerConnection.ontrack = () => { };
        const peerConnectionNotClosed = peerConnection.signalingState !== "closed";
        let dataChannelNotClosed = false;
        if (this.connection.type === enums_1.ConnectionType.Data) {
            const dataConnection = this.connection;
            const dataChannel = dataConnection.dataChannel;
            if (dataChannel) {
                dataChannelNotClosed = !!dataChannel.readyState && dataChannel.readyState !== "closed";
            }
        }
        if (peerConnectionNotClosed || dataChannelNotClosed) {
            peerConnection.close();
        }
    }
    _makeOffer() {
        return __awaiter(this, void 0, void 0, function* () {
            const peerConnection = this.connection.peerConnection;
            const provider = this.connection.provider;
            try {
                const offer = yield peerConnection.createOffer(this.connection.options.constraints);
                logger_1.default.log("Created offer.");
                if (this.connection.options.sdpTransform && typeof this.connection.options.sdpTransform === 'function') {
                    offer.sdp = this.connection.options.sdpTransform(offer.sdp) || offer.sdp;
                }
                try {
                    yield peerConnection.setLocalDescription(offer);
                    logger_1.default.log("Set localDescription:", offer, `for:${this.connection.peer}`);
                    let payload = {
                        sdp: offer,
                        type: this.connection.type,
                        connectionId: this.connection.connectionId,
                        metadata: this.connection.metadata,
                        browser: util_1.util.browser,
                        config: this.connection.provider.options.config
                    };
                    if (this.connection.type === enums_1.ConnectionType.Data) {
                        const dataConnection = this.connection;
                        payload = Object.assign(Object.assign({}, payload), { label: dataConnection.label, reliable: dataConnection.reliable, serialization: dataConnection.serialization });
                    }
                    provider.socket.send({
                        type: enums_1.ServerMessageType.Offer,
                        payload,
                        dst: this.connection.peer
                    });
                }
                catch (err) {
                    // TODO: investigate why _makeOffer is being called from the answer
                    if (err !=
                        "OperationError: Failed to set local offer sdp: Called in wrong state: kHaveRemoteOffer") {
                        provider.emitError(enums_1.PeerErrorType.WebRTC, err);
                        logger_1.default.log("Failed to setLocalDescription, ", err);
                    }
                }
            }
            catch (err_1) {
                provider.emitError(enums_1.PeerErrorType.WebRTC, err_1);
                logger_1.default.log("Failed to createOffer, ", err_1);
            }
        });
    }
    _makeAnswer() {
        return __awaiter(this, void 0, void 0, function* () {
            const peerConnection = this.connection.peerConnection;
            const provider = this.connection.provider;
            try {
                const answer = yield peerConnection.createAnswer();
                logger_1.default.log("Created answer.");
                if (this.connection.options.sdpTransform && typeof this.connection.options.sdpTransform === 'function') {
                    answer.sdp = this.connection.options.sdpTransform(answer.sdp) || answer.sdp;
                }
                try {
                    yield peerConnection.setLocalDescription(answer);
                    logger_1.default.log(`Set localDescription:`, answer, `for:${this.connection.peer}`);
                    provider.socket.send({
                        type: enums_1.ServerMessageType.Answer,
                        payload: {
                            sdp: answer,
                            type: this.connection.type,
                            connectionId: this.connection.connectionId,
                            browser: util_1.util.browser
                        },
                        dst: this.connection.peer
                    });
                }
                catch (err) {
                    provider.emitError(enums_1.PeerErrorType.WebRTC, err);
                    logger_1.default.log("Failed to setLocalDescription, ", err);
                }
            }
            catch (err_1) {
                provider.emitError(enums_1.PeerErrorType.WebRTC, err_1);
                logger_1.default.log("Failed to create answer, ", err_1);
            }
        });
    }
    /** Handle an SDP. */
    handleSDP(type, sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            sdp = new RTCSessionDescription(sdp);
            const peerConnection = this.connection.peerConnection;
            const provider = this.connection.provider;
            logger_1.default.log("Setting remote description", sdp);
            const self = this;
            try {
                yield peerConnection.setRemoteDescription(sdp);
                logger_1.default.log(`Set remoteDescription:${type} for:${this.connection.peer}`);
                if (type === "OFFER") {
                    yield self._makeAnswer();
                }
            }
            catch (err) {
                provider.emitError(enums_1.PeerErrorType.WebRTC, err);
                logger_1.default.log("Failed to setRemoteDescription, ", err);
            }
        });
    }
    /** Handle a candidate. */
    handleCandidate(ice) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.default.log(`handleCandidate:`, ice);
            const candidate = ice.candidate;
            const sdpMLineIndex = ice.sdpMLineIndex;
            const sdpMid = ice.sdpMid;
            const peerConnection = this.connection.peerConnection;
            const provider = this.connection.provider;
            try {
                yield peerConnection.addIceCandidate(new RTCIceCandidate({
                    sdpMid: sdpMid,
                    sdpMLineIndex: sdpMLineIndex,
                    candidate: candidate
                }));
                logger_1.default.log(`Added ICE candidate for:${this.connection.peer}`);
            }
            catch (err) {
                provider.emitError(enums_1.PeerErrorType.WebRTC, err);
                logger_1.default.log("Failed to handleCandidate, ", err);
            }
        });
    }
    _addTracksToConnection(stream, peerConnection) {
        logger_1.default.log(`add tracks from stream ${stream.id} to peer connection`);
        if (!peerConnection.addTrack) {
            return logger_1.default.error(`Your browser does't support RTCPeerConnection#addTrack. Ignored.`);
        }
        stream.getTracks().forEach(track => {
            peerConnection.addTrack(track, stream);
        });
    }
    _addStreamToMediaConnection(stream, mediaConnection) {
        logger_1.default.log(`add stream ${stream.id} to media connection ${mediaConnection.connectionId}`);
        mediaConnection.addStream(stream);
    }
}
exports.Negotiator = Negotiator;
